import React, { useCallback, useEffect } from 'react';
import { GlobalSkipLinks } from './partials/GlobalSkipLinks';
import { ErrorBoundary } from '../../../_lib/components/ErrorBoundary/ErrorBoundary';
import { FooterSection } from '../../../_sections/Footer';
import { MainLayoutSwitcher } from '../../../_layouts';
import { hideAllTooltips } from '../../../components/atoms/Tooltip';
import { GlobalMenuState, useGlobalMenu } from '../../../contexts/GlobalMenuStore';
import { HeaderSection } from '../../../_sections/Header';
import { NavigationSection } from '../../../_sections/Navigation/NavigationSection';
import { BreadcrumbSection } from '../../../_sections/Breadcrumb/BreadcrumbSection';
import { PersonalSection } from '../../../_sections/Personal/PersonalSection';
import { Composition } from '../../../types/api/Composition';
import { useCached } from '../../../hooks/useCached';
import { KeyEvent } from '../../../types/KeyEvent';
import { GlobalDocumentIds } from '../../../types/GlobalDocumentIds';

type Frame = {
  composition: Composition;
};

export function Frame({ composition }: Frame) {
  const { currentOpenMenu, closeMenus } = useGlobalMenu();

  /**
   * "global" Keyevent Handler that is responsible for closing any opened
   * menu and Tooltip
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === KeyEvent.Escape) {
        if (currentOpenMenu !== GlobalMenuState.NONE) {
          closeMenus();
        }
        hideAllTooltips();
      }
    },
    [currentOpenMenu, closeMenus]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const navigationSection = useCached(composition.sections.navigation);
  const breadcrumbSection = useCached(composition.sections.breadcrumb);

  const personalSection = useCached(composition.sections.personal);
  const footerSection = useCached(composition.sections.footer);
  const pageManagementSection = useCached(composition.sections.pageManagement);

  return (
    <div className="page-container">
      <GlobalSkipLinks />
      <div className="wrapper">
        {composition.sections.header && <HeaderSection {...composition.sections.header} showToggles />}
        <div className="wrapper__innercontent">
          <ErrorBoundary>
            <NavigationSection {...navigationSection} />
          </ErrorBoundary>
          <div className="wrapper__content" id={GlobalDocumentIds.mainContentWrapper}>
            <div
              className={composition?.pageInfo?.printable === true ? 'content' : 'content noprint'}
              id={GlobalDocumentIds.scrollContent}
            >
              {/* As we do not scroll the document anymore (SON-17145), ReturnToTopArrow.tsx needs a scroll target. */}
              {/* Empty anchors without content and href attribute as scroll targets are perfectly valid HTML, btw. */}
              {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
              <a className="scroll-top-anchor" id={GlobalDocumentIds.scrollTopAnchor}></a>
              {breadcrumbSection && (
                <BreadcrumbSection breadcrumb={breadcrumbSection} pageManagement={pageManagementSection} />
              )}
              <MainLayoutSwitcher
                layout={composition.layout}
                contentSection={composition.sections.content}
                contentMultiColumn={composition.sections.contentMultiColumn}
                contextSection={composition.sections.context}
              />
              <FooterSection {...footerSection} />
            </div>
          </div>
          <ErrorBoundary>
            <PersonalSection {...personalSection} />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}
